import {
    Component,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { CdkStep } from '@angular/cdk/stepper';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { isAPIApplicationException } from '../../../../core/helpers';
import {
    CaptchaAction,
    CaptchaError,
    CaptchaService
} from '../../../../core/services';
import {
    AccountStepperComponent,
    AccountVerificationComponent,
    LoginComponent
} from '../../components';
import { AuthService } from '../../services';

@Component({
    selector: 'app-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslocoModule,
        RouterModule,
        AccountStepperComponent,
        LoginComponent,
        AccountVerificationComponent,
        MatIcon,
        CdkStep,
        FlexLayoutModule,
        MatButtonModule,
        MatFormFieldModule,
        MatDialogModule
    ]
})
export class SignInComponent implements OnInit {
    private _emailOrPhone: string | undefined;

    @ViewChild(AccountStepperComponent) stepper:
        | AccountStepperComponent
        | undefined;

    @ViewChild(LoginComponent) login: LoginComponent | undefined;

    loginBusy = false;

    /** The CAPTCHA could not be completed, so no code was requested. */
    codeRequestFailed = false;

    constructor(
        private readonly _auth: AuthService,
        private readonly _captcha: CaptchaService
    ) {}

    ngOnInit(): void {
        this._captcha.preload(CaptchaAction.SendSignInCode);
    }

    emailOrPhoneProvided(emailOrPhone: string) {
        // A second request would restart the challenge the first one is waiting on.
        if (this.loginBusy) return;

        this._emailOrPhone = emailOrPhone;
        this.loginBusy = true;
        this.codeRequestFailed = false;
        this._auth
            .requestSignInCode(this._emailOrPhone)
            .pipe(
                tap(() => this.stepper?.next()),
                catchError((e) => this.handleCodeRequestError(e)),
                finalize(() => (this.loginBusy = false))
            )
            .subscribe();
    }

    codeProvided(code: number) {
        if (this._emailOrPhone)
            this._auth.signIn(this._emailOrPhone, code).subscribe();
    }

    goBack(): void {
        this.stepper?.previous();
    }

    /**
     * ApiService rethrows the response body, so an API application exception arrives as the
     * problem details object. Other HTTP failures have already been reported by
     * HttpErrorInterceptor, which leaves only a CAPTCHA that could not be completed.
     */
    private handleCodeRequestError(e: any): Observable<never> {
        if (isAPIApplicationException(e)) {
            this.login?.emailOrPhone?.setErrors({
                invalidEmailOrPhoneNumber: true
            });
        } else if (e instanceof CaptchaError) {
            this.codeRequestFailed = true;
        }
        return EMPTY;
    }
}
